import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { SECTORS } from '@/data/sector-data';
import { SocialLinks } from '@/components/ui/SocialLinks';

// Keep in step with buildSeoFooterHtml() in functions/_middleware.ts, which
// server-renders the same links and notice into #seo-footer for crawlers that
// don't run JS.
export const OPERATOR_NAME = 'Creon Solutions';
export const OPERATOR_URL = 'https://www.creonsolutions.com';
export const SUPPORT_EMAIL = 'support@creonsolutions.com';
// Wording follows Terms §2 ("sourced from third parties and/or public CSE data",
// "may be delayed") and the "Not Investment Advice" notice — don't strengthen it
// here without changing the Terms.
export const FOOTER_NOTICE =
  'Market data comes from public Colombo Stock Exchange sources and third parties, and may be delayed or contain errors. For information only — not investment advice.';

const externalProps = { target: '_blank', rel: 'noopener' } as const;

// The footer replaces the crawler-only copy the server put in #seo-footer, so
// once a client footer is on screen that copy is redundant.
function useDropSeoFooter() {
  useEffect(() => {
    document.getElementById('seo-footer')?.remove();
  }, []);
}

/** Full footer for document-style pages (sector, legal, about, methodology). */
export function SiteFooter() {
  useDropSeoFooter();

  return (
    <footer className="mt-14 pt-8 pb-10 border-t border-border">
      <div className="grid gap-8 text-sm md:grid-cols-[1.1fr_1.9fr_0.8fr_0.8fr_0.8fr]">
        <div>
          <div className="font-semibold text-text mb-1.5">CeylonCharts</div>
          <p className="text-xs leading-relaxed text-muted mb-2">
            Charts, screener and market data for the Colombo Stock Exchange.
          </p>
          <p className="text-xs leading-relaxed text-muted">
            Operated by{' '}
            <a href={OPERATOR_URL} {...externalProps} className="text-accent hover:underline">{OPERATOR_NAME}</a>
            <br />
            <a href={`mailto:${SUPPORT_EMAIL}`} className="text-accent hover:underline">{SUPPORT_EMAIL}</a>
          </p>
          <SocialLinks className="mt-3" />
        </div>

        <nav aria-label="Sectors">
          <div className="text-[10px] uppercase tracking-wider text-muted mb-1">Sectors</div>
          <ul className="columns-2 gap-5 text-xs leading-[1.95]">
            {SECTORS.map((s) => (
              <li key={s.slug} className="break-inside-avoid">
                <Link to={`/sector/${s.slug}`} className="text-accent hover:underline">{s.name}</Link>
              </li>
            ))}
          </ul>
        </nav>

        <nav aria-label="Product">
          <div className="text-[10px] uppercase tracking-wider text-muted mb-1">Product</div>
          <ul className="flex flex-col text-xs leading-[1.95]">
            <li><Link to="/chart/ASI" className="text-accent hover:underline">Live charts</Link></li>
            <li><Link to="/sectors" className="text-accent hover:underline">All sectors</Link></li>
            <li><Link to="/methodology" className="text-accent hover:underline">Methodology</Link></li>
            <li><Link to="/mcp" className="text-accent hover:underline">MCP server</Link></li>
            <li><Link to="/about" className="text-accent hover:underline">About</Link></li>
          </ul>
        </nav>

        <nav aria-label="Blog">
          <div className="text-[10px] uppercase tracking-wider text-muted mb-1">Blog</div>
          <ul className="flex flex-col text-xs leading-[1.95]">
            <li><a href="/blog/guides" className="text-accent hover:underline">Guides</a></li>
            <li><a href="/blog/learn" className="text-accent hover:underline">Learn</a></li>
            <li><a href="/blog/market" className="text-accent hover:underline">Market</a></li>
            <li><a href="/blog/rss.xml" className="text-accent hover:underline">RSS feed</a></li>
          </ul>
        </nav>

        <nav aria-label="Legal">
          <div className="text-[10px] uppercase tracking-wider text-muted mb-1">Legal</div>
          <ul className="flex flex-col text-xs leading-[1.95]">
            <li><Link to="/terms" className="text-accent hover:underline">Terms of Service</Link></li>
            <li><Link to="/privacy" className="text-accent hover:underline">Privacy Policy</Link></li>
          </ul>
        </nav>
      </div>

      <p className="mt-6 pt-4 border-t border-border text-[11px] leading-relaxed text-muted">{FOOTER_NOTICE}</p>
    </footer>
  );
}

/**
 * One-line footer for the chart terminal, which fills the window and has no
 * room for a document footer. The sector list lives in a menu that is always in
 * the DOM (just not displayed while closed) so the links stay crawlable.
 */
export function FooterStrip() {
  useDropSeoFooter();

  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    // Capture phase: the chart canvas handles its own pointer events and would
    // otherwise swallow an outside click before it reached document.
    const onPointerDown = (e: PointerEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const link = 'hover:text-text transition-colors';

  return (
    <footer className="shrink-0 flex items-center justify-between gap-4 h-7 px-3 border-t border-border bg-panel text-[11px] leading-none text-muted">
      <span className="truncate min-w-0">{FOOTER_NOTICE}</span>

      <nav aria-label="Site" className="flex items-center gap-3.5 shrink-0">
        <Link to="/about" className={link}>About</Link>
        <Link to="/methodology" className={link}>Methodology</Link>
        <Link to="/mcp" className={link}>MCP</Link>
        <a href="/blog" className={link}>Blog</a>
        <Link to="/terms" className={link}>Terms</Link>
        <Link to="/privacy" className={link}>Privacy</Link>

        <div ref={menuRef} className="relative">
          <button
            type="button"
            aria-expanded={open}
            aria-controls="footer-sectors"
            onClick={() => setOpen((v) => !v)}
            className={`${open ? 'text-accent' : link} cursor-pointer`}
          >
            Sectors {open ? '▾' : '▴'}
          </button>
          <ul
            id="footer-sectors"
            className={`${open ? 'grid' : 'hidden'} absolute right-0 bottom-full mb-2 grid-cols-2 gap-x-6 px-4 py-3 w-max max-w-[92vw] glass-modal rounded-xl z-dropdown text-xs leading-[1.9] text-modal-text`}
          >
            {SECTORS.map((s) => (
              <li key={s.slug}>
                <Link to={`/sector/${s.slug}`} onClick={() => setOpen(false)} className="hover:text-accent transition-colors">
                  {s.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </nav>
    </footer>
  );
}
